import { SearchBar } from '@acin/ui-core';
import { SelectOption } from '@acin/ui-core/types/src/components/Select/components/Select';
import React from 'react';
import { IFilterGroupName, IFilterType, IInternalRegulationDocumentInstance } from '../../../graphql/generated';
import { generate, TestIdProp } from '../../../helpers/testId';
import { useRegulation } from '../../RegulationProvider';
import { useInternalDocumentInstancesQuery } from '../graphql/queries/getDocumentsByRegulation.generated';
import { useInternalFilterOptionsByFilterGroupQuery } from '../graphql/queries/internalFilterOptionsByFilterGroup.generated';
import { useAllDocuments } from '../hooks/useAllDocuments';
import { useDocumentFilters } from '../hooks/useDocumentFilters';
import * as SC from './Documents.styled';
import { DocumentsList } from './DocumentsList';

export interface DocumentsProps extends TestIdProp {
  className?: string;
  regulationId: string;
  offerAllDocuments?: boolean;
  selectedDocument?: IInternalRegulationDocumentInstance;
  onSelectDocument: (document?: IInternalRegulationDocumentInstance) => void;
  statusField?: 'obligationReviewStatus' | 'mappingsReviewStatus';
}

export const Documents: React.FC<DocumentsProps> = (props) => {
  const { className, regulationId, offerAllDocuments, selectedDocument, onSelectDocument, statusField } = props;
  const [filters, setFilters] = React.useState(undefined);
  const [sort, setSort] = React.useState(undefined);

  const { onUpdateActiveDocument, onUpdateDocumentsInitialised } = useRegulation();

  const {
    searchTerm,
    status,
    date,
    handleSearch,
    handleClearSearch,
    handleStatusChange,
    handleDateChange,
    getFilteredDocuments,
  } = useDocumentFilters(statusField); // Why did we need to use a provider for this - i cannot remember the specific reason for the need.
  const [initialDocuments, setInitialDocuments] = React.useState<IInternalRegulationDocumentInstance[]>([]);

  const {
    data: filterData,
    error: filterError,
    loading: filterLoading,
  } = useInternalFilterOptionsByFilterGroupQuery({
    variables: {
      input: {
        filterGroupCode: IFilterGroupName.DocumentSelectionModal,
      },
    },
    fetchPolicy: 'no-cache',
  });

  const {
    data: documentData,
    error: documentError,
    loading: documentLoading,
  } = useInternalDocumentInstancesQuery({
    variables: {
      input: {
        filters: {
          groups: [
            {
              groupKey: 'REGULATION_ID',
              filterValues: [
                {
                  value: regulationId,
                },
              ],
            },
          ],
        },
      },
    },
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      const docs = result.internalDocumentInstances.edges;

      if (docs.length === 1) {
        onUpdateActiveDocument(docs[0].node);
      }
      onUpdateDocumentsInitialised(true);
    },
  });

  const documents = documentData?.internalDocumentInstances?.edges.map((item) => item.node) ?? [];
  const documentsIncludingAllOption = useAllDocuments(documents, initialDocuments, offerAllDocuments, statusField);
  const filteredDocuments = getFilteredDocuments(documentsIncludingAllOption);

  React.useEffect(() => {
    if (documentData?.internalDocumentInstances?.edges && !initialDocuments.length) {
      setInitialDocuments(documentData?.internalDocumentInstances.edges.map((item) => item.node));
    }
  }, [documentData?.internalDocumentInstances?.edges, initialDocuments, regulationId]);

  const ALL_OPTION: SelectOption = { value: 'ALL', label: 'All' };

  const timestampFilter = filterData?.internalFilterOptionsByFilterGroup?.allFilters.find(
    (group) => group.id === 'REGULATION_DOCUMENT_CREATED_TIMESTAMP',
  );
  const dateOptions: SelectOption[] = [
    ALL_OPTION,
    ...(timestampFilter?.type === IFilterType.List &&
    timestampFilter.attributes &&
    'filterGroups' in timestampFilter.attributes
      ? timestampFilter.attributes.filterGroups
          .flatMap((group) => group.availableFilters.map((f) => ({ value: f.value, label: f.label ?? '' })))
          .filter((opt, index, arr) => arr.findIndex((o) => o.value === opt.value) === index)
      : []),
  ];

  const statusFilter = filterData?.internalFilterOptionsByFilterGroup?.allFilters.find(
    (group) => group.id === 'REGULATION_DOCUMENT_STATUS',
  );
  const statusOptions: SelectOption[] = [
    ALL_OPTION,
    ...(statusFilter?.type === IFilterType.List && statusFilter.attributes && 'filterGroups' in statusFilter.attributes
      ? statusFilter.attributes.filterGroups
          .flatMap((group) => group.availableFilters.map((f) => ({ value: f.value, label: f.label ?? '' })))
          .filter((opt, index, arr) => arr.findIndex((o) => o.value === opt.value) === index)
      : []),
  ];

  return (
    <SC.Documents data-testid={generate(['documents'], props)} className={className}>
      <SC.Header>
        <SearchBar
          searchInput={searchTerm}
          onClear={handleClearSearch}
          onSearch={handleSearch}
          placeholder="Search by document ID or name"
          squareCorners
        />

        <SC.DateDropdown
          onChange={handleDateChange}
          options={dateOptions}
          value={date ?? 'ALL'}
          prefix="Issuance Date"
        />
        <SC.StatusDropdown
          onChange={handleStatusChange}
          options={statusOptions}
          value={status ?? 'ALL'}
          prefix="Status"
        />
      </SC.Header>

      <DocumentsList
        loading={documentLoading}
        error={documentError}
        documents={filteredDocuments}
        selectedDocument={selectedDocument}
        onSelectDocument={onSelectDocument}
        statusField={statusField}
      />
    </SC.Documents>
  );
};
